import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { loginRequest, extractRoleFromToken } from "../config/authConfig";
import toast from "react-hot-toast";
import { useState } from "react";

interface MSALError {
  message?: string;
  errorCode?: string;
}

export default function EntraIdLogin() {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const setToken = useAuthStore((state) => state.setToken);
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setLoading(true);
    try {
      const response = await instance.loginPopup(loginRequest);
      const role = extractRoleFromToken((response.idTokenClaims || {}) as Record<string, unknown>);
      const email = response.account?.username || "Unknown";
      toast.success(`Welcome ${response.account?.name || email}!`);
      setToken(response.idToken, role, "entra-id", email);
      navigate("/");
    } catch (error) {
      const msalError = error as MSALError;
      if (msalError?.message?.includes("does not have required admin role")) {
        toast.error("Access denied: You do not have admin privileges");
      } else if (msalError?.errorCode === "user_cancelled") {
        // User closed the popup, just reset loading state
        console.log("Login cancelled by user");
      } else {
        toast.error(`Login failed: ${msalError.message || "Unknown error"}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded shadow">
        <div>
          <h2 className="text-center text-3xl font-extrabold text-gray-900">
            Admin Portal
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Sign in with your organization account
          </p>
        </div>

        <div className="mt-8">
          <button
            onClick={handleLogin}
            disabled={loading}
            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-400 disabled:cursor-not-allowed"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M0 0h10v10H0V0zm10 0h10v10H10V0zM0 10h10v10H0V10zm10 0h10v10H10V10z" />
            </svg>
            {loading ? "Signing in..." : "Sign in with Microsoft"}
          </button>
        </div>

        <div className="mt-4 text-xs text-gray-500 text-center">
          <p>Secure authentication via Microsoft Entra ID</p>
          <p className="mt-1">Only authorized administrators can access this portal</p>
        </div>
      </div>
    </div>
  );
}
